import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { filter, map, merge, tap, withLatestFrom } from "rxjs";
import { SessionActions } from "../../../features/authentication/store/session.actions";
import { sessionFeature } from "../../../features/authentication/store/session.reducer";
import { CallActions } from "../../../features/calls/store/call.actions";
import { callFeature } from "../../../features/calls/store/call.reducer";
import { IncomingCallMessage } from "../realtime.models";
import { SocketTransportService } from "../socket-transport.service";
import { RealtimeActions } from "./realtime.actions";
import { realtimeFeature } from "./realtime.reducer";

@Injectable()
export class RealtimeEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly transport = inject(SocketTransportService);

    readonly connect$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                SessionActions.currentUserLoaded,
                SessionActions.guestSessionStarted
            ),
            filter(({ user }) => user.role === "user"),
            tap(({ user }) => this.transport.connect(user)),
            map(() => RealtimeActions.connectionRequested())
        )
    );

    readonly disconnectPortalUsers$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(SessionActions.currentUserLoaded),
                filter(
                    ({ user }) =>
                        user.role === "owner" || user.role === "partner"
                ),
                tap(() => this.transport.disconnect())
            ),
        { dispatch: false }
    );

    readonly reconnect$ = createEffect(
        () => this.actions$.pipe(
            ofType(RealtimeActions.reconnectRequested),
            withLatestFrom(
                this.store.select(sessionFeature.selectUser),
                this.store.select(sessionFeature.selectStatus),
                this.store.select(realtimeFeature.selectMultipleRegistration),
                this.store.select(realtimeFeature.selectStatus)
            ),
            filter(([, user, status, conflict, connection]) => user?.role === "user" && status === "authenticated" && !conflict && connection !== "connected"),
            tap(([, user]) => {
                this.transport.disconnect();
                this.transport.connect(user!);
            })
        ),
        { dispatch: false }
    );

    readonly disconnect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    SessionActions.logoutRequested,
                    SessionActions.authenticationFailed,
                    SessionActions.noStoredCredentials,
                    SessionActions.guestSessionEnded
                ),
                tap(() => this.transport.disconnect())
            ),
        { dispatch: false }
    );

    readonly endGuestCall$ = createEffect(() => this.actions$.pipe(
        ofType(SessionActions.guestSessionEnded),
        map(() => CallActions.callEnded()),
    ));

    readonly connectionStatus$ = createEffect(() =>
        this.transport.connection$.pipe(
            map((status) => RealtimeActions.connectionStatusChanged({ status }))
        )
    );

    readonly eventAudit$ = createEffect(() =>
        this.transport.events$.pipe(
            map(({ name }) =>
                RealtimeActions.messageReceived({ eventName: name })
            )
        )
    );

    readonly guestWaiting$ = createEffect(() => this.transport.on<{ roomId: string; username?: string }>("guest_waiting").pipe(
        withLatestFrom(this.store.select(sessionFeature.selectUser)),
        filter(([message, user]) => !!user && !user.isGuest && typeof message?.roomId === "string"),
        map(([message]) => {
            const suffix = message.username?.split("@gemvision.io-")[1];
            const room = message.roomId.includes("/") ? message.roomId : suffix ? `${message.roomId}/${suffix}` : "";
            return RealtimeActions.guestWaitingReceived({ room });
        }),
        filter(action => action.room.split("/").length === 2 && action.room.split("/").every(Boolean)),
    ));

    readonly protocolEvents$ = createEffect(() =>
        merge(
            this.transport
                .on<unknown>("user_register_result")
                .pipe(map(() => RealtimeActions.userRegistered())),
            this.transport
                .on<unknown>("user_multiregister")
                .pipe(
                    map(() => RealtimeActions.multipleRegistrationDetected())
                ),
            this.transport
                .on<unknown>("multiregister")
                .pipe(
                    map(() => RealtimeActions.multipleRegistrationDetected())
                ),
            this.transport
                .on<unknown>("join")
                .pipe(map(() => RealtimeActions.roomJoinSignaled())),
            this.transport
                .on<unknown>("joined")
                .pipe(map(() => RealtimeActions.roomJoined())),
            this.transport
                .on<unknown>("user_joined")
                .pipe(map(() => RealtimeActions.roomUserJoined()))
        )
    );

    readonly registrationRoom$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.userRegistered),
            withLatestFrom(
                this.store.select(realtimeFeature.selectCurrentRoom),
                this.store.select(sessionFeature.selectUser)
            ),
            filter(([, , user]) => Boolean(user)),
            map(([, room, user]) => {
                const resolvedRoom = room?.trim() || user!.username;
                this.transport.emit("create_or_join", resolvedRoom);
                return RealtimeActions.createOrJoinRoomEmitted({
                    room: resolvedRoom,
                });
            })
        )
    );

    readonly restorePresenceAfterRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.userRegistered),
            withLatestFrom(this.store.select(callFeature.selectCallsState)),
            map(([, call]) =>
                call.status !== "idle" && call.status !== "failed"
                    ? RealtimeActions.presenceForcedBusy()
                    : RealtimeActions.presenceRestoreRequested()
            )
        )
    );

    readonly selectPresence$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.presenceSelectionRequested),
            tap(({ presence }) =>
                this.transport.emit(
                    "user_status_update",
                    presence === "available" ? "available" : "unavailable"
                )
            ),
            map(({ presence }) =>
                RealtimeActions.presenceChanged({ presence, remember: true })
            )
        )
    );

    readonly forceBusy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.presenceForcedBusy),
            tap(() => this.transport.emit("user_status_update", "unavailable")),
            map(() =>
                RealtimeActions.presenceChanged({
                    presence: "busy",
                    remember: false,
                })
            )
        )
    );

    readonly restorePresence$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.presenceRestoreRequested),
            withLatestFrom(
                this.store.select(realtimeFeature.selectPreferredPresence)
            ),
            tap(([, presence]) =>
                this.transport.emit(
                    "user_status_update",
                    presence === "available" ? "available" : "unavailable"
                )
            ),
            map(([, presence]) =>
                RealtimeActions.presenceChanged({ presence, remember: false })
            )
        )
    );

    readonly createOrJoinRoom$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.createOrJoinRoomRequested),
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            filter(([, user]) => Boolean(user)),
            map(([{ room }, user]) => {
                const resolvedRoom = room?.trim() || user!.username;
                this.transport.emit("create_or_join", resolvedRoom);
                return RealtimeActions.createOrJoinRoomEmitted({
                    room: resolvedRoom,
                });
            })
        )
    );

    readonly multipleRegistration$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.multipleRegistrationDetected),
            tap(() => this.transport.disconnect()),
            map(() => CallActions.callEnded())
        )
    );

    readonly restoreSession$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RealtimeActions.restoreSessionRequested),
            withLatestFrom(this.store.select(sessionFeature.selectUser)),
            map(([, user]) => {
                if (!user) return SessionActions.logoutRequested();
                if (user.role !== "user") {
                    this.transport.disconnect();
                    return RealtimeActions.connectionStatusChanged({
                        status: "disconnected",
                    });
                }
                this.transport.connect(user);
                return RealtimeActions.connectionRequested();
            })
        )
    );

    readonly incomingCalls$ = createEffect(() =>
        this.transport.on<IncomingCallMessage>("init_call_req").pipe(
            withLatestFrom(
                this.store.select(sessionFeature.selectUser),
                this.store.select(callFeature.selectCallsState)
            ),
            filter(([message, user, callState]) =>
                Boolean(
                    user &&
                    callState.status === "idle" &&
                    message?.caller &&
                    message?.callee &&
                    message.callee.username === user.username &&
                    message.caller.username !== message.callee.username
                )
            ),
            map(([message]) =>
                CallActions.invitationReceived({
                    invitation: {
                        source: "socket",
                        id: message.callId,
                        audioOnly: message.audioOnly,
                        caller: message.caller,
                        callee: message.callee,
                        participant: {
                            id: message.caller.userId,
                            name:
                                message.caller.name || message.caller.username,
                            organization: message.caller.companyId || "",
                        },
                    },
                })
            )
        )
    );

    readonly remoteCallEnd$ = createEffect(() =>
        merge(
            this.transport.on<unknown>("bye").pipe(map(() => "bye")),
            this.transport
                .on<unknown>("cancelled")
                .pipe(map(() => "cancelled")),
            this.transport.on<unknown>("timedout").pipe(map(() => "timedout")),
            this.transport.on<unknown>("ended").pipe(map(() => "ended"))
        ).pipe(
            withLatestFrom(this.store.select(callFeature.selectCallsState)),
            filter(([, state]) => state.status !== "idle"),
            map(([eventName]) => CallActions.remoteEndReceived({ eventName }))
        )
    );
}
